import { copy } from '@ohear/shared'
/**
 * Block-recording mode picker — Pro Tools segmented control.
 *
 * Lives in the script-panel header. Buttons offer:
 *   📦 Auto-cluster (B) — accept system suggestion
 *   🎬 Scene block  (G) — record full scene end-to-end
 *   ⌖ Range        (L) — record selected range
 *   💥 Effort       (E) — combat / breath continuous
 *
 * Each button uses .pt-btn for the bevel, with .pt-btn--armed adding the
 * yellow inset glow when active (classic PT armed-state look).
 */
import { cx } from './cx'
import type { BlockMode } from './state'

export interface BlockModeButtonsProps {
  /** Total cues in the active scene that belong to the actor. */
  sceneCueCount: number
  /** Current scene label (e.g. "Scene 03 · Gate Night Battle"). */
  sceneLabel: string
  /** Currently armed mode (none if 'single'). */
  active: BlockMode
  onChoose: (mode: NonNullable<BlockMode>) => void
  /** Hide range button if no multi-cue range is selected. */
  rangeAvailable: boolean
  locale?: 'zh' | 'en'
}

interface SegmentProps {
  active: boolean
  disabled?: boolean
  onClick: () => void
  icon: string
  label: string
  shortcut: string
  title: string
  /** True when this segment is the first/last in the group (for joined corners). */
  first?: boolean
  last?: boolean
}

function Segment({
  active,
  disabled,
  onClick,
  icon,
  label,
  shortcut,
  title,
  first,
  last,
}: SegmentProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-pressed={active}
      className={cx(
        'pt-btn pt-btn--armed',
        'flex flex-1 items-center justify-center gap-1.5 px-2.5 py-1.5 text-[11px]',
        '-ml-px first:ml-0',
        first && 'rounded-l-sm rounded-r-none',
        last && 'rounded-r-sm rounded-l-none',
        !first && !last && 'rounded-none',
      )}
    >
      <span aria-hidden="true">{icon}</span>
      <span className="whitespace-nowrap">{label}</span>
      <span className="pt-kbd ml-1">{shortcut}</span>
    </button>
  )
}

export function BlockModeButtons({
  sceneCueCount,
  sceneLabel,
  active,
  onChoose,
  rangeAvailable,
  locale = 'en',
}: BlockModeButtonsProps) {
  const sceneOffer = copy.block.sceneBlockOffer(sceneLabel)
  const effortOffer = copy.block.EFFORT_OFFER
  const autoOffer = copy.block.autoClusterOffer(sceneCueCount)

  return (
    <div
      className="inline-flex items-stretch overflow-hidden rounded-sm"
      role="group"
      aria-label="Recording block mode"
    >
      <Segment
        active={active === 'auto'}
        onClick={() => onChoose('auto')}
        icon="🧩"
        label="Auto"
        shortcut="B"
        title={locale === 'zh' ? autoOffer.zh : autoOffer.en}
        first
      />
      <Segment
        active={active === 'scene'}
        onClick={() => onChoose('scene')}
        icon="📦"
        label="Scene"
        shortcut="G"
        title={locale === 'zh' ? sceneOffer.zh : sceneOffer.en}
      />
      <Segment
        active={active === 'range'}
        disabled={!rangeAvailable}
        onClick={() => onChoose('range')}
        icon="⌖"
        label="Range"
        shortcut="L"
        title="Shift-click two of your cues to define a range"
      />
      <Segment
        active={active === 'effort'}
        onClick={() => onChoose('effort')}
        icon="💥"
        label="Effort"
        shortcut="E"
        title={locale === 'zh' ? effortOffer.zh : effortOffer.en}
        last
      />
    </div>
  )
}
